import React, { FC, useMemo } from 'react'

import { Dropdown } from '@style-port/roov-ui-components-react'
import { Icon, Tooltip, Txt } from 'src/components/atoms'

import { ProfileEntity, WorksRoleType } from 'src/types'

type Props = {
  worksRoles: WorksRoleType[]
  profile: ProfileEntity
  value: number
  selectOption: (value: number) => void
  disabled?: boolean
  size?: 's' | 'm' | 'l'
  className?: string
}

export const WorksRoleDropdown: FC<Props> = ({
  worksRoles,
  profile,
  value,
  selectOption,
  disabled = false,
  size,
  className,
}) => {
  const displayRoles = useMemo(() => {
    // 社外ユーザーは選択させない
    const baseRoles = [...worksRoles]
      .filter(role => role.name !== 'external')
      .sort((a, b) => {
        // メンバー・管理者・全権管理者 の順で表示する
        if (a.name === 'member') {
          return -1
        }
        if (b.name === 'member') {
          return 1
        }
        if (a.name === 'admin') {
          return b.name === 'admin' ? 1 : -1
        }
        return b.name === 'admin' || b.name === 'admin' ? -1 : 1
      })

    // プロジェクト管理者の場合、システム管理者は選択出来ないようにする
    return profile.role.name === 'admin'
      ? baseRoles.filter(role => role.name !== 'superAdmin')
      : baseRoles
  }, [worksRoles, profile])

  const loadIcon = (roleId: string) => {
    if (roleId === 'superAdmin') {
      return 'monitor/soft/line'
    }
    if (roleId === 'admin') {
      return 'tool/soft/line'
    }
    return 'user/soft/line'
  }

  const MemberTooltipTxt = (
    <Txt sizeType="s">
      <b>メンバー</b>
      <br />
      特定のプロジェクトへアサインされると、
      <br />
      ROOV compassへアクセスが可能。
    </Txt>
  )
  const AdminTooltipTxt = (
    <Txt sizeType="s">
      <b>管理者</b>
      <br />
      「メンバー」役割に加え、プロジェクトの作成が可能。
    </Txt>
  )
  const SuperAdminTooltipTxt = (
    <Txt sizeType="s">
      <b>全権管理者</b>
      <br />
      「管理者」役割に加え、「全権管理設定」の操作が可能
      <br />
      となり、すべて権限が実行可能。
    </Txt>
  )

  const loadTooltipTxt = (roleName: string) => {
    if (roleName === 'superAdmin') {
      return SuperAdminTooltipTxt
    }
    if (roleName === 'admin') {
      return AdminTooltipTxt
    }
    return MemberTooltipTxt
  }

  return (
    <div className={className}>
      <Dropdown
        value={value}
        disabled={disabled}
        options={displayRoles.map(role => {
          return {
            value: role.id,
            children: (
              <Tooltip placement="right" showIcon={false} content={loadTooltipTxt(role.name)}>
                <div className="tw-flex tw-items-center">
                  <Icon
                    path={loadIcon(role.name)}
                    className="tw-text-standardText-disable"
                    size="--r0"
                  />
                  <Txt className="tw-ml-s-1">{role.displayName}</Txt>
                </div>
              </Tooltip>
            ),
          }
        })}
        selectOption={option => selectOption(option.value as number)}
        size={size}
      />
    </div>
  )
}
